import React from "react";
import { scoreColor, ratingColor, formatNumber, formatDate } from "@/lib/format";
import { Star, CheckSquare, Square, Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export function KpiCard({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-border p-5 hover:border-primary/50 transition-colors">
      <div className="overline-label text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export function ScoreBadge({ score, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm font-mono ${scoreColor(score)}`}>
      {score}
    </span>
  );
}

export function RatingCell({ rating }) {
  if (rating == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-medium ${ratingColor(rating)}`}>
      <Star size={12} weight="fill" /> {rating.toFixed(1)}
    </span>
  );
}

export function AppRow({ a, selectable, selected, onToggle }) {
  return (
    <tr className="border-b border-border/60 hover:bg-accent/50 transition-colors">
      {selectable && (
        <td className="px-3 py-2.5 w-8">
          <button data-testid={`app-select-${a.package_name}`} onClick={() => onToggle(a.package_name)}
                  disabled={a.is_lead}
                  className={`p-0.5 rounded-sm ${a.is_lead ? "opacity-30 cursor-not-allowed" : "hover:bg-accent"}`}>
            {selected ? <CheckSquare size={16} weight="fill" className="text-primary" /> : <Square size={16} />}
          </button>
        </td>
      )}
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-3">
          {a.icon ? (
            <img src={a.icon} alt="" className="w-8 h-8 rounded-sm border border-border" />
          ) : (
            <div className="w-8 h-8 rounded-sm bg-muted" />
          )}
          <div className="min-w-0">
            <Link to={`/apps/${a.package_name}`} className="font-medium text-sm hover:underline block truncate max-w-[220px]"
                  data-testid={`app-link-${a.package_name}`}>
              {a.name}
            </Link>
            <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[220px]">{a.package_name}</div>
          </div>
          {a.is_lead && (
            <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-sm flex items-center gap-0.5">
              <Check size={9} weight="bold" /> lead
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-sm">
        <Link to={`/developers/${encodeURIComponent(a.developer_id || a.developer)}`}
              className="hover:underline">{a.developer}</Link>
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">{a.category}</td>
      <td className="px-3 py-2.5 text-right"><RatingCell rating={a.rating} /></td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">{formatNumber(a.review_count)}</td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">{a.install_range}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">{formatDate(a.first_seen)}</td>
      <td className="px-3 py-2.5 text-right"><ScoreBadge score={a.lead_score ?? 0} testid={`score-${a.package_name}`} /></td>
    </tr>
  );
}

export function AppsTable({ apps, selectable = false, selectedSet = null, onToggle = null, onToggleAll = null }) {
  const eligible = apps?.filter(a => !a.is_lead) || [];
  const allSelected = selectable && selectedSet && eligible.length > 0 && eligible.every(a => selectedSet.has(a.package_name));
  return (
    <div className="border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/60 backdrop-blur border-b border-border sticky top-0">
          <tr className="text-left overline-label text-muted-foreground">
            {selectable && (
              <th className="px-3 py-2.5 w-8">
                <button data-testid="app-select-all" onClick={() => onToggleAll?.(eligible, allSelected)}
                        className="p-0.5 hover:bg-accent rounded-sm">
                  {allSelected ? <CheckSquare size={14} weight="fill" className="text-primary" /> : <Square size={14} />}
                </button>
              </th>
            )}
            <th className="px-3 py-2.5">App</th>
            <th className="px-3 py-2.5">Developer</th>
            <th className="px-3 py-2.5">Category</th>
            <th className="px-3 py-2.5 text-right">Rating</th>
            <th className="px-3 py-2.5 text-right">Reviews</th>
            <th className="px-3 py-2.5 text-right">Installs</th>
            <th className="px-3 py-2.5">First Seen</th>
            <th className="px-3 py-2.5 text-right">Score</th>
          </tr>
        </thead>
        <tbody>
          {apps?.length ? apps.map((a) => (
            <AppRow key={a.package_name} a={a} selectable={selectable}
                    selected={selectedSet?.has(a.package_name)} onToggle={onToggle} />
          )) : (
            <tr><td colSpan={selectable ? 9 : 8} className="px-3 py-12 text-center text-sm text-muted-foreground">
              No apps found. Try running a discovery from Admin, or search a specific package.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function SectionTitle({ eyebrow, title, actions }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <div className="overline-label text-muted-foreground">{eyebrow}</div>
        <h2 className="font-heading font-black text-2xl mt-1">{title}</h2>
      </div>
      <div className="flex gap-2">{actions}</div>
    </div>
  );
}
